import { useEffect, useState } from "react";
import axios from "axios";
import { Check, X as XIcon, RefreshCw, Users, BedDouble, Trash2, Download } from "lucide-react";
import { toast } from "sonner";
import { useAdminAuth, LoginScreen } from "./adminAuth";
import AdminHeader from "../components/AdminHeader";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDashboard() {
  const { password, authed, checking, login, logout } = useAdminAuth();
  const [rsvps, setRsvps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const load = async () => {
    if (!password) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/rsvps`, { params: { password } });
      setRsvps(res.data || []);
    } catch (err) {
      toast.error("Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  };

  const remove = async (rsvp) => {
    if (!window.confirm(`Zusage von "${rsvp.name}" wirklich löschen?\n\nDieser Vorgang kann nicht rückgängig gemacht werden.`)) return;
    setDeletingId(rsvp.id);
    try {
      await axios.delete(`${API}/rsvps/${rsvp.id}`, { params: { password } });
      setRsvps((p) => p.filter((r) => r.id !== rsvp.id));
      toast.success(`"${rsvp.name}" gelöscht`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Löschen fehlgeschlagen");
    } finally {
      setDeletingId(null);
    }
  };

  useEffect(() => {
    if (authed) load();
    // eslint-disable-next-line
  }, [authed]);

  if (checking) return <div className="min-h-screen bg-sage-50" />;
  if (!authed) return <LoginScreen onSubmit={login} />;

  const yes = rsvps.filter((r) => r.attending?.toLowerCase() === "ja");
  const no = rsvps.filter((r) => r.attending?.toLowerCase() !== "ja");
  const totalGuests = yes.reduce((s, r) => s + (r.guests || 0) + 1, 0);
  const hotelRequests = rsvps.filter((r) => (r.hotel || "").toLowerCase() === "ja").length;
  const hotelMaybe = rsvps.filter((r) => (r.hotel || "").toLowerCase() === "vielleicht").length;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-sage-50">
      <AdminHeader onLogout={logout} />

      <main className="container mx-auto px-6 md:px-12 py-10">
        <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
          <h2 className="font-serif text-2xl md:text-3xl text-sage-800">Zusagen-Übersicht</h2>
          <div className="flex gap-2">
            <a
              data-testid="admin-export-csv"
              href={`${API}/rsvps/export.csv?password=${encodeURIComponent(password)}`}
              className="inline-flex items-center gap-2 rounded-full border border-sage-300 text-sage-700 hover:bg-sage-100 px-4 py-2 text-xs uppercase tracking-wide"
            >
              <Download size={14} />
              CSV-Export
            </a>
            <button
              data-testid="admin-refresh-btn"
              onClick={load}
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-full border border-sage-300 text-sage-700 hover:bg-sage-100 px-4 py-2 text-xs uppercase tracking-wide"
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
              Aktualisieren
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-10">
          <StatCard label="Zusagen" value={yes.length} icon={<Check size={20} />} testid="stat-yes" />
          <StatCard label="Absagen" value={no.length} icon={<XIcon size={20} />} testid="stat-no" />
          <StatCard label="Gäste insgesamt" value={totalGuests} icon={<Users size={20} />} testid="stat-total" />
          <StatCard
            label="Zimmer gewünscht"
            value={`${hotelRequests}${hotelMaybe ? ` (+${hotelMaybe}?)` : ""}`}
            icon={<BedDouble size={20} />}
            testid="stat-hotel"
          />
        </div>

        <div
          data-testid="admin-rsvp-table"
          className="bg-white rounded-2xl border border-sage-200 overflow-hidden shadow-sm"
        >
          {rsvps.length === 0 ? (
            <div className="p-12 text-center text-sage-500">
              Noch keine Zusagen eingegangen.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-sage-100/70 text-sage-700">
                  <tr>
                    <Th>Name</Th>
                    <Th>Status</Th>
                    <Th>Begleitung</Th>
                    <Th>Hotel</Th>
                    <Th>Ernährung / Nachricht</Th>
                    <Th>Eingang</Th>
                    <Th></Th>
                  </tr>
                </thead>
                <tbody>
                  {rsvps.map((r, i) => (
                    <tr
                      key={r.id}
                      data-testid={`admin-rsvp-row-${i}`}
                      className="border-t border-sage-100 hover:bg-sage-50/60"
                    >
                      <Td>
                        <div className="font-medium text-sage-800">{r.name}</div>
                        {r.email && (
                          <a
                            href={`mailto:${r.email}`}
                            className="text-xs text-sage-500 hover:text-sage-700 underline"
                          >
                            {r.email}
                          </a>
                        )}
                      </Td>
                      <Td>
                        <StatusBadge attending={r.attending} />
                      </Td>
                      <Td>{r.guests || 0}</Td>
                      <Td>
                        <HotelBadge hotel={r.hotel} />
                      </Td>
                      <Td>
                        <div className="max-w-sm">
                          {r.dietary && (
                            <div className="text-xs">
                              <span className="uppercase tracking-wide text-sage-500">Ernährung: </span>
                              <span className="text-sage-800">{r.dietary}</span>
                            </div>
                          )}
                          {r.message && (
                            <div className="mt-1 text-xs text-sage-700 italic">
                              „{r.message}"
                            </div>
                          )}
                          {!r.dietary && !r.message && (
                            <span className="text-xs text-sage-400">—</span>
                          )}
                        </div>
                      </Td>
                      <Td>
                        <span className="text-sage-600 text-xs whitespace-nowrap">
                          {new Date(r.created_at).toLocaleString("de-DE")}
                        </span>
                      </Td>
                      <Td>
                        <button
                          data-testid={`admin-rsvp-delete-${i}`}
                          onClick={() => remove(r)}
                          disabled={deletingId === r.id}
                          className="inline-flex items-center justify-center rounded-lg bg-white border border-red-200 text-red-500 hover:bg-red-50 disabled:opacity-50 p-2 transition-colors"
                          aria-label={`Zusage von ${r.name} löschen`}
                          title="Löschen"
                        >
                          <Trash2 size={14} />
                        </button>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

const Th = ({ children }) => (
  <th className="text-left px-5 py-3 text-xs uppercase tracking-[0.18em] font-medium">
    {children}
  </th>
);
const Td = ({ children }) => <td className="px-5 py-4 align-top">{children}</td>;

const StatCard = ({ label, value, icon, testid }) => (
  <div
    data-testid={testid}
    className="bg-white rounded-2xl border border-sage-200 p-5 flex items-center gap-4 shadow-sm"
  >
    <div className="w-12 h-12 rounded-full bg-sage-100 flex items-center justify-center text-sage-600">
      {icon}
    </div>
    <div>
      <div className="text-xs uppercase tracking-[0.2em] text-sage-500">{label}</div>
      <div className="font-serif text-3xl text-sage-800">{value}</div>
    </div>
  </div>
);

const StatusBadge = ({ attending }) => {
  const isYes = attending?.toLowerCase() === "ja";
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium ${
        isYes ? "bg-sage-100 text-sage-700" : "bg-stone-100 text-stone-600"
      }`}
    >
      {isYes ? <Check size={12} /> : <XIcon size={12} />}
      {isYes ? "Kommt" : "Absage"}
    </span>
  );
};

const HotelBadge = ({ hotel }) => {
  const h = (hotel || "nein").toLowerCase();
  const cfg = {
    ja: { label: "Zimmer", cls: "bg-sage-500 text-white" },
    vielleicht: { label: "Vielleicht", cls: "bg-sage-200 text-sage-800" },
    nein: { label: "Nein", cls: "bg-stone-100 text-stone-500" },
  }[h] || { label: "Nein", cls: "bg-stone-100 text-stone-500" };
  return (
    <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
};
